import React from 'react';
import Icon from '../../../components/AppIcon';

const ComparisonMatrix = ({ comparisonData }) => {
  const getMethodIcon = (methodType) => {
    const icons = {
      'VA Transfer': 'Building2',
      'QRIS': 'QrCode',
      'OVO': 'Wallet',
      'DANA': 'Wallet',
      'GoPay': 'Wallet',
      'Card': 'CreditCard'
    };
    return icons?.[methodType] || 'DollarSign';
  };

  const getMethodColor = (methodType) => {
    const colors = {
      'VA Transfer': 'bg-blue-500',
      'QRIS': 'bg-purple-500',
      'OVO': 'bg-indigo-500',
      'DANA': 'bg-cyan-500',
      'GoPay': 'bg-green-500',
      'Card': 'bg-orange-500'
    };
    return colors?.[methodType] || 'bg-gray-500';
  };

  const getStatusColor = (value, metric) => {
    if (metric === 'successRate') {
      if (value >= 95) return 'text-success';
      if (value >= 85) return 'text-warning';
      return 'text-error';
    }
    if (metric === 'settlementTime') {
      if (value <= 2) return 'text-success';
      if (value <= 24) return 'text-warning';
      return 'text-error';
    }
    if (metric === 'userPreference') {
      if (value >= 4) return 'text-success';
      if (value >= 3) return 'text-warning';
      return 'text-error';
    }
    return 'text-foreground';
  };

  const metrics = [
    { key: 'successRate', label: 'Success Rate', suffix: '%', icon: 'CheckCircle2' },
    { key: 'failureReason', label: 'Top Failure', suffix: '', icon: 'AlertCircle' },
    { key: 'settlementTime', label: 'Settlement', suffix: 'h', icon: 'Clock' },
    { key: 'userPreference', label: 'User Rating', suffix: '/5', icon: 'Star' }
  ];

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div>
          <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
            Comprehensive Comparison Matrix
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground mt-1 md:mt-2">
            Detailed performance metrics across all payment methods
          </p>
        </div>
        <Icon name="Grid3x3" size={20} className="text-muted-foreground md:w-6 md:h-6" />
      </div>
      <div className="overflow-x-auto -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full divide-y divide-border">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-card px-3 py-3 md:px-4 md:py-4 text-left text-xs md:text-sm font-semibold text-foreground">
                  Payment Method
                </th>
                {metrics?.map((metric) => (
                  <th
                    key={metric?.key}
                    className="px-3 py-3 md:px-4 md:py-4 text-left text-xs md:text-sm font-semibold text-foreground whitespace-nowrap"
                  >
                    <div className="flex items-center gap-2">
                      <Icon name={metric?.icon} size={14} className="md:w-4 md:h-4" />
                      {metric?.label}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {comparisonData?.map((method) => (
                <tr
                  key={method?.name}
                  className="hover:bg-muted/50 transition-colors duration-250"
                >
                  <td className="sticky left-0 z-10 bg-card px-3 py-3 md:px-4 md:py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div
                        className={`w-8 h-8 md:w-10 md:h-10 rounded-lg ${getMethodColor(
                          method?.name
                        )} flex items-center justify-center flex-shrink-0`}
                      >
                        <Icon
                          name={getMethodIcon(method?.name)}
                          size={16}
                          color="white"
                          className="md:w-5 md:h-5"
                        />
                      </div>
                      <span className="text-xs md:text-sm font-medium text-foreground">
                        {method?.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3 md:px-4 md:py-4 whitespace-nowrap">
                    <span
                      className={`text-sm md:text-base font-semibold ${getStatusColor(
                        method?.successRate,
                        'successRate'
                      )}`}
                    >
                      {method?.successRate}%
                    </span>
                  </td>
                  <td className="px-3 py-3 md:px-4 md:py-4">
                    <span className="text-xs md:text-sm text-muted-foreground">
                      {method?.failureReason}
                    </span>
                  </td>
                  <td className="px-3 py-3 md:px-4 md:py-4 whitespace-nowrap">
                    <span
                      className={`text-sm md:text-base font-semibold ${getStatusColor(
                        method?.settlementTime,
                        'settlementTime'
                      )}`}
                    >
                      {method?.settlementTime}h
                    </span>
                  </td>
                  <td className="px-3 py-3 md:px-4 md:py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 md:gap-2">
                      <span
                        className={`text-sm md:text-base font-semibold ${getStatusColor(
                          method?.userPreference,
                          'userPreference'
                        )}`}
                      >
                        {method?.userPreference?.toFixed(1)}
                      </span>
                      <Icon
                        name="Star"
                        size={14}
                        className={`${getStatusColor(
                          method?.userPreference,
                          'userPreference'
                        )} md:w-4 md:h-4`}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-border">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-lg bg-success/10">
            <Icon name="CheckCircle2" size={20} className="text-success md:w-6 md:h-6" />
            <div>
              <p className="text-xs md:text-sm text-muted-foreground">Best Success Rate</p>
              <p className="text-sm md:text-base font-semibold text-foreground">
                {Math.max(...comparisonData?.map((m) => m?.successRate))}%
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-lg bg-warning/10">
            <Icon name="Clock" size={20} className="text-warning md:w-6 md:h-6" />
            <div>
              <p className="text-xs md:text-sm text-muted-foreground">Fastest Settlement</p>
              <p className="text-sm md:text-base font-semibold text-foreground">
                {Math.min(...comparisonData?.map((m) => m?.settlementTime))}h
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-lg bg-info/10">
            <Icon name="Star" size={20} className="text-info md:w-6 md:h-6" />
            <div>
              <p className="text-xs md:text-sm text-muted-foreground">Highest Rating</p>
              <p className="text-sm md:text-base font-semibold text-foreground">
                {Math.max(...comparisonData?.map((m) => m?.userPreference))?.toFixed(1)}/5
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-lg bg-primary/10">
            <Icon name="TrendingUp" size={20} className="text-primary md:w-6 md:h-6" />
            <div>
              <p className="text-xs md:text-sm text-muted-foreground">Avg Performance</p>
              <p className="text-sm md:text-base font-semibold text-foreground">
                {(comparisonData?.reduce((sum, m) => sum + m?.successRate, 0) / comparisonData?.length)?.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonMatrix;